import { useNavigate } from "react-router-dom";
import { Flex, Text, Heading, Spacer, useDisclosure } from "@chakra-ui/react";
import { WithAuth } from "@/hoc";
import { useEffect, useState } from "react";
import { useCreditCard } from "@/hooks/useCreditCard";
import { formatCardStatus, formatFinalNumbers } from "@/utils";
import NewCreditCard from "@/pages/cartoes/novo-cartao";
import { CreditCardInvoice } from "../fatura-cartao";
import { DynamicTable } from "@/components/DynamicTable";
import { FillLockIcon, PlusCircle, SettingsIcon, UnlockIcon } from "@/styles/icons";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { StatusModal } from "@/components";
import { InvoiceIcon } from "@/styles/icons/InvoiceIcon";
import ConfigCreditCard from "../configurar-cartao";
import { Button, PageHeading } from "barte-design-system";

const ManageCreditCardPage = () => {
  const navigate = useNavigate();
  const [isNewCreditCardFormOpen, setIsNewCreditCardFormOpen] = useState(false);
  const featureTogglesUser = localStorage?.getItem("barte-features-list-user");
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  function formatCardStatusIcon(status: string) {
    const cardStatusIcon = {
      ACTIVE: <UnlockIcon size={14}  />,
      BLOCKED: <FillLockIcon size={14}  />,
      PENDING: <FillLockIcon size={14}  />
    }
    return cardStatusIcon[status] || <FillLockIcon size={14}  />;
  }
  const columns = [
    {
      key: "panMasked",
      title: "Cartão",
      dataIndex: "panMasked",
      sorter: false,
      render: (panMasked) => (
        <Flex align="center" gridGap="2">
          <Text>{`final `}</Text>
          <Text
            fontWeight={600}
          >
            {formatFinalNumbers(panMasked)}
          </Text>
        </Flex>
      ),
    },
    {
      key: "cardName",
      title: "Nome",
      dataIndex: "cardName",
      sorter: true,
      render: (cardName) => (
        <Flex align="center" gridGap="2">
          <Text>{cardName}</Text>
        </Flex>
      ),
    },
    {
      key: "",
      title: "",
      dataIndex: "",
      sorter: false,
      emptyText: true,
    },
    {
      key: "",
      title: "",
      dataIndex: "",
      sorter: false,
      emptyText: true,
    },
    {
      key: "cardStatus",
      title: "Status",
      dataIndex: "cardStatus",
      sorter: true,
      render: (cardStatus) => (
        <Flex align="center" gridGap="2">
            {formatCardStatusIcon(cardStatus)}
            <Text
              fontWeight={400}
              fontSize="12px"
            >
              {formatCardStatus(cardStatus)}
            </Text>
        </Flex>
      ),
    },
  ];

  const { 
    getInitialData, 
    creditCards, 
    setSelectedCreditCardName, 
    initialDataIsLoading,
    creditCardLimit,
    downloadCreditCardStatement,
    currentCreditCardInvoiceDate,
  } = useCreditCard();
  const currentBalance = creditCardLimit.invoiceValue ?? 0;
  const availableLimit = creditCardLimit.availableValueVirtualCard ?? 0; 
  const totalLimit = creditCardLimit.totalValueBag ?? 0;
  const usedPercentage = (currentBalance / totalLimit) * 100;
  const { loading } = useAsyncFunction(downloadCreditCardStatement);
  const { isOpen, onClose } = useDisclosure();
  const { onOpen: onOpenConfigCreditCard, isOpen: isOpenConfigCreditCard, onClose: onCloseConfigCreditCard } = useDisclosure();

  useEffect(() => {
    getInitialData();
  } , []);

  const handleNewCreditCard = () => {
    if(creditCards?.length === 0) {
      onOpenConfigCreditCard();
      return
    }
    setIsNewCreditCardFormOpen(!isNewCreditCardFormOpen);
  }

  return (
    <>
      <StatusModal 
        isOpen={isOpen}
        status="success"
        message="O extrato será enviado para o seu e-mail."
        onClose={onClose}
      />
      <Flex 
        w="100%" 
        justify="center" 
        align="center"
        flexDir="column"
      >
        <PageHeading
          title="Cartão de crédito"
          secondaryActionLabel={user?.roles?.includes("B_SELLER_ADMIN") || user?.roles?.includes("BANKING_ADMIN") || user?.roles?.includes("BANKING_CARD") ? "Gerenciar limites" : null}
          onSecondaryAction={() => navigate("/cartao/limites")}
          primaryActionLabel={user?.roles?.includes("B_SELLER_ADMIN") || user?.roles?.includes("BANKING_ADMIN") || user?.roles?.includes("BANKING_CARD") ? "Novo cartão" : null}
          onPrimaryAction={handleNewCreditCard}
        />
        <ConfigCreditCard
          isOpen={isOpenConfigCreditCard}
          onClose={() => {
            onCloseConfigCreditCard()
            setIsNewCreditCardFormOpen(!isNewCreditCardFormOpen)
          }}
        />
        <NewCreditCard 
          isOpen={isNewCreditCardFormOpen}
          onClose={() => setIsNewCreditCardFormOpen(!isNewCreditCardFormOpen)}
        />
        <Flex 
            flexDir="column"
            width="100%"
            gap="1rem"
            py="4"
        >
          
          <Flex
            w="100%"
            gap="2"
            align="end"
          >
            {(user?.roles?.includes("B_SELLER_ADMIN") || user?.roles?.includes("BANKING_ADMIN") || user?.roles?.includes("BANKING_CARD")) && (
              <>
                <Button
                  variant="outline"
                  onClick={() => { navigate("/cartao/faturas")}}
                  disabled={featureTogglesUser?.includes("block_items_credit_card") || loading}
                  loading={loading}
                >
                  Ver faturas
                </Button>
              </>
            )}
            <Spacer />
            <CreditCardInvoice 
              availableLimit={availableLimit}
              usedPercentage={usedPercentage}
              currentBalance={currentBalance}
              dueDate={currentCreditCardInvoiceDate.dueDate}
              nextClosingDate={currentCreditCardInvoiceDate.endDate}
              status={currentCreditCardInvoiceDate.status}
              isLimitProgressVisible
              customHeight="140px"
            />
          </Flex>
          <DynamicTable
              columns={columns}
              data={creditCards}
              rowKey={(record) => record.id}
              onRowClick={(record) => {
                if(record?.cardStatus === "PENDING") {
                  return;
                }
                setSelectedCreditCardName(record?.cardName)
                navigate(`/cartao/${record.cardId}`)
              }}
              locale={{ emptyText: "Sem dados de cartões de crédito." }}
              loading={initialDataIsLoading}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default WithAuth(ManageCreditCardPage);
